import React, { useState, useEffect } from 'react';
import './Cupones.scss';
import { getMenuOptions, getItemOptions, getCategoryOptions, createOptionMap } from '../utils';
import { Card, Button } from 'react-bootstrap';
import { formatCurrency } from '../../../../../../utils/functions';
import { FaPen, FaQrcode, FaTrash } from 'react-icons/fa';
import CreateClientCoupon from './components/CreateClientCoupon/CreateClientCoupon';
import { FaArrowRotateRight } from 'react-icons/fa6';
import { FaExclamationTriangle } from 'react-icons/fa';
import UpdateCouponDate from './components/UpdateCouponDate/UpdateCouponDate';
import UpdateCoupon from './components/UpdateCoupon/UpdateCoupon';
import DeleteCoupon from './components/DeleteCoupon/DeleteCoupon';
import CreateCoupon from './components/CreateCoupon/CreateCoupon';
import { getPermissionState } from '../../../../../../utils/functions';

interface CouponsProps {
  cupones: any[];
  setCupones: (cupones: any[]) => void;
  showCreateCouponModal: boolean;
  setShowCreateCouponModal: (show: boolean) => void;
  showUpdateCouponModal: boolean;
  setShowUpdateCouponModal: (show: boolean) => void;
  dynamicallySelectedCoupons: any[];
  toggleCouponSelection: (coupon: any) => void;
  selectionMode: boolean; // Para saber si se está en modo selección
  showClientCoupon: boolean;
  setShowClientCoupon: (show: boolean) => void;
  selectedCoupon: any;
  setSelectedCoupon: (coupon: any) => void;
}

const Cupones: React.FC<CouponsProps> = ({
  cupones,
  setCupones,
  showCreateCouponModal,
  setShowCreateCouponModal,
  showUpdateCouponModal,
  setShowUpdateCouponModal,
  dynamicallySelectedCoupons,
  toggleCouponSelection,
  selectionMode,
  showClientCoupon,
  setShowClientCoupon,
  selectedCoupon,
  setSelectedCoupon
}) => {
  const [itemOptions, setItemOptions] = useState<any[]>([]);
  const [menuOptions, setMenuOptions] = useState<any[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [itemMap, setItemMap] = useState<any>({});
  const [menuMap, setMenuMap] = useState<any>({});
  const [categoryMap, setCategoryMap] = useState<any>({});

  // Estado para el modal UpdateDateCoupon (nueva modal para actualizar la fecha)
  const [showUpdateDateCouponModal, setShowUpdateDateCouponModal] = useState(false);

  // Estados para el modal de eliminación
  const [showDeleteCouponsModal, setShowDeleteCouponsModal] = useState(false);
  const [couponsToDelete, setCouponsToDelete] = useState<any[]>([]);

  useEffect(() => {
    getMenuOptions().then((response) => {
      if (response) {
        setMenuOptions(response);
        setMenuMap(createOptionMap(response));
      }
    });
    getCategoryOptions().then((response) => {
      if (response) {
        setCategoryOptions(response);
        setCategoryMap(createOptionMap(response));
      }
    });
    getItemOptions().then((response) => {
      if (response) {
        setItemOptions(response);
        setItemMap(createOptionMap(response));
      }
    });
  }, []);

  function formatDateTime(date: string) {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('es-ES', { timeZone: 'UTC' });
  }

  // Función para verificar si el cupón ha vencido
function isExpired(coupon: any): boolean {
  if (!coupon.expiration_date) return false;

  const now = new Date();
  const couponExpiration = new Date(coupon.expiration_date);
  // Extiende la fecha de expiración un día más
  couponExpiration.setDate(couponExpiration.getDate() + 1);

  return couponExpiration < now;
}


  function getCouponScope(coupon: any) {
    if (coupon.isglobal) return 'En cualquier item';
    if (coupon.menu_id) return `En items del menú ${menuMap[coupon.menu_id]}`;
    if (coupon.menu_category_id) return `En items de la categoría ${categoryMap[coupon.menu_category_id]}`;
    if (coupon.item_id) return `En ${itemMap[coupon.item_id]}`;
    return 'Local';
  }

  function getAmountOrPercentage(coupon: any) {
    if (coupon.coupon_amount) return `${formatCurrency(coupon.coupon_amount)}`;
    if (coupon.coupon_percentage) return `${coupon.coupon_percentage}%`;
    return 'No especificado';
  }

  function getLimitConditions(coupon: any) {
    // Si se tiene presupuesto máximo
    if (coupon.max_budget) {
      return (
        <div className="coupon-limit-conditions">
          <p className="coupon-limit-text">
            {formatCurrency(coupon.current_budget_used)} usado de {formatCurrency(coupon.max_budget)}
          </p>
        </div>
      );
    }
    // Si se tiene límite de número de cupones
    if (coupon.max_number) {
      return (
        <div className="coupon-limit-conditions">
          <p className="coupon-limit-text">
            {coupon.current_number_used} cupones emitidos de {coupon.max_number}
          </p>
        </div>
      );
    }
    return null;
  }

  const handleShowUpdateCouponModal = (coupon: any) => {
    setSelectedCoupon(coupon);
    setShowUpdateCouponModal(true);
  };

  const handleShowClientCouponModal = (coupon: any) => {
    setSelectedCoupon(coupon);
    setShowClientCoupon(true);
  };

  // Si no estás en modo selección se abrirá el modal de eliminación individual
  const handleDeleteCouponClick = (coupon: any) => {
    if (selectionMode) {
      toggleCouponSelection(coupon);
    } else {
      setCouponsToDelete([coupon]);
      setShowDeleteCouponsModal(true);
    }
  };

  return (
    <>
      <div className="cupones-container">
        {cupones.map((coupon, index) => (
          <Card
            key={index}
            className={`coupon-card 
              ${dynamicallySelectedCoupons.includes(coupon) ? 'selected' : ''} 
              ${isExpired(coupon) ? 'expired' : 'valid'}`}
            onClick={() => {
              if (selectionMode) {
                toggleCouponSelection(coupon);
              }
            }}
          >
            <Card.Body>
              {/* Main Content */}
              <div className="coupon-card-main">
                <Card.Title className="coupon-card-title">
                  {coupon.payment_coupon_name}
                </Card.Title>
                <p className="coupon-amount-percentage">{getAmountOrPercentage(coupon)}</p>
                <p className="coupon-scope-text">{getCouponScope(coupon)}</p>
              </div>

              {/* Validity Info */}
              {coupon.expiration_date && (
                <div className={`coupon-validity-badge ${isExpired(coupon) ? 'expired' : ''}`}>
                  {isExpired(coupon) ? (
                    <>
                      <FaExclamationTriangle className="validity-icon" />
                      <span>Cupón vencido</span>
                    </>
                  ) : (
                    <span>Válido hasta {formatDateTime(new Date(new Date(coupon.expiration_date).setDate(new Date(coupon.expiration_date).getDate() - 1)).toISOString())}</span>
                  )}
                </div>
              )}
            </Card.Body>

            {getLimitConditions(coupon)}

            <Card.Footer className="coupon-card-footer">
              <div className="coupon-stats-row">
                <div className="coupon-stat">
                  <span className="stat-value">{coupon.current_number_used}</span>
                  <span className="stat-label">emitidos</span>
                </div>
                <div className="coupon-stat">
                  <span className="stat-value">{formatCurrency(coupon.current_budget_used)}</span>
                  <span className="stat-label">usados</span>
                </div>
              </div>
              
              {/* Action buttons in footer */}
              {!selectionMode && (
                <div className="coupon-action-buttons">
                  <Button
                    variant="none"
                    className="action-button edit-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShowUpdateCouponModal(coupon);
                    }}
                    title="Editar cupón"
                    disabled={!getPermissionState(97)}
                  >
                    <FaPen />
                  </Button>
                  <Button
                    variant="none"
                    className="action-button delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteCouponClick(coupon);
                    }}
                    title="Eliminar cupón"
                    disabled={!getPermissionState(98)}
                  >
                    <FaTrash />
                  </Button>
                  {isExpired(coupon) ? (
                    <Button
                      variant="none"
                      className="action-button renew-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCoupon(coupon);
                        setShowUpdateDateCouponModal(true);
                      }}
                      title="Renovar cupón"
                    >
                      <FaArrowRotateRight />
                    </Button>
                  ) : (
                    <Button
                      variant="none"
                      className="action-button qr-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShowClientCouponModal(coupon);  
                      }}
                      title="Generar código QR"
                    >
                      <FaQrcode />
                    </Button>
                  )}
                </div>
              )}
            </Card.Footer>
          </Card>
        ))}
      </div>

      {/* Crear Cupón */}
      <CreateCoupon
        show={showCreateCouponModal}
        setShowCreateCouponModal={setShowCreateCouponModal}
        handleClose={() => setShowCreateCouponModal(false)}
        setCoupon={setCupones}
        coupons={cupones}
        itemOptions={itemOptions}
        categoryOptions={categoryOptions}
        menuOptions={menuOptions}
      />

      {/* Asignar Cupón a Cliente */}
      {showClientCoupon && (
        <CreateClientCoupon
          show={showClientCoupon}
          setShow={setShowClientCoupon}
          selectedCoupon={selectedCoupon}
          setSelectedCoupon={setSelectedCoupon}
          Maps={{ itemMap, menuMap, categoryMap }}
        />
      )}

      {/* Actualizar Cupón */}
      {showUpdateCouponModal && (
        <UpdateCoupon
          show={showUpdateCouponModal}
          handleClose={() => setShowUpdateCouponModal(false)}
          coupon={selectedCoupon}
          coupons={cupones}
          setCoupon={setCupones}
          itemOptions={itemOptions}
          categoryOptions={categoryOptions}
          menuOptions={menuOptions}
        />
      )}

      {/* Eliminar Cupón */}
      {showDeleteCouponsModal && (
        <DeleteCoupon
          show={showDeleteCouponsModal}
          handleClose={() => setShowDeleteCouponsModal(false)}
          couponsToDelete={couponsToDelete}
          setCoupons={setCupones}
        />
      )}

      {/* Actualizar Fecha (Modal de la flecha circular) */}
      {showUpdateDateCouponModal && (
        <UpdateCouponDate
          show={showUpdateDateCouponModal}
          handleClose={() => setShowUpdateDateCouponModal(false)}
          coupon={selectedCoupon}
          coupons={cupones}
          setCoupon={setCupones}
        />
      )}
    </>
  );
};

export default Cupones;
